/**
 * ModulesSection — Collapsible module checkbox grid
 *
 * Shows available modules as checkboxes.
 * Supports search filtering from parent searchQuery.
 */

import { useMemo, useState, useEffect } from "react";
import { Grid, Checkbox, FormControlLabel } from "@mui/material";
import ExtensionIcon from "@mui/icons-material/Extension";

import KDBox from "components/KDBox";
import KDTypography from "components/KDTypography";
import CollapsibleSection from "templates/FullPageDetailTemplate/CollapsibleSection";
import { useMaterialUIController } from "context";
import { useT } from "context/I18nContext";
import useApi from "hooks/useApi";
import { endpoints } from "config/api";

function ModulesSection({
  roleData,
  updateField,
  expanded,
  onToggle,
  canEdit,
  searchQuery,
}) {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const t = useT();
  const { get } = useApi();

  // Fetch available modules from server registry
  const [availableModules, setAvailableModules] = useState([]);
  useEffect(() => {
    let cancelled = false;
    get(endpoints.registry.modules).then((data) => {
      if (!cancelled) {
        const ids = (data.modules || data || []).map((m) => (typeof m === "string" ? m : m.id));
        setAvailableModules(ids);
      }
    }).catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const modules = roleData?.modules || [];

  // Filter modules by search
  const filteredModules = useMemo(() => {
    if (!searchQuery?.trim()) return availableModules;
    const q = searchQuery.toLowerCase();
    return availableModules.filter((m) => m.toLowerCase().includes(q));
  }, [searchQuery, availableModules]);

  const toggleModule = (mod) => {
    if (modules.includes(mod)) {
      updateField("modules", modules.filter((m) => m !== mod));
    } else {
      updateField("modules", [...modules, mod]);
    }
  };

  const toggleAll = () => {
    const allChecked = availableModules.every((m) => modules.includes(m));
    if (allChecked) {
      updateField("modules", []);
    } else {
      updateField("modules", [...availableModules]);
    }
  };

  const allChecked = availableModules.every((m) => modules.includes(m));
  const someChecked = modules.length > 0 && !allChecked;

  return (
    <CollapsibleSection
      title={t("roles.modules")}
      icon={<ExtensionIcon sx={{ color: "white", fontSize: 20 }} />}
      iconGradient={["#22C55E", "#86EFAC"]}
      badge={modules.length}
      expanded={expanded}
      onToggle={onToggle}
    >
      <KDTypography variant="body2" color="text" mb={2} sx={{ opacity: 0.7 }}>
        {t("roles.detail.modulesDescription")}
      </KDTypography>

      {/* Select all */}
      <KDBox
        sx={{
          mb: 2,
          pb: 1.5,
          borderBottom: darkMode
            ? "1px solid rgba(255,255,255,0.08)"
            : "1px solid rgba(0,0,0,0.08)",
        }}
      >
        <FormControlLabel
          control={
            <Checkbox
              checked={allChecked}
              indeterminate={someChecked}
              onChange={toggleAll}
              disabled={!canEdit}
              size="small"
              sx={darkMode ? {
                color: "rgba(255,255,255,0.3)",
                "&.Mui-checked": { color: "#22C55E" },
                "&.MuiCheckbox-indeterminate": { color: "#22C55E" },
              } : {
                "&.Mui-checked": { color: "#22C55E" },
                "&.MuiCheckbox-indeterminate": { color: "#22C55E" },
              }}
            />
          }
          label={
            <KDTypography
              variant="subtitle2"
              fontWeight="bold"
              color={darkMode ? "white" : "dark"}
            >
              {t("common.all")} ({modules.length}/{availableModules.length})
            </KDTypography>
          }
        />
      </KDBox>

      {/* Module grid */}
      <Grid container spacing={1}>
        {filteredModules.map((mod) => (
          <Grid item xs={12} sm={6} md={4} key={mod}>
            <KDBox
              sx={{
                p: 1.5,
                borderRadius: "12px",
                border: modules.includes(mod)
                  ? darkMode
                    ? "1px solid rgba(34,197,94,0.4)"
                    : "1px solid rgba(34,197,94,0.3)"
                  : darkMode
                    ? "1px solid rgba(255,255,255,0.08)"
                    : "1px solid rgba(0,0,0,0.06)",
                backgroundColor: modules.includes(mod)
                  ? darkMode
                    ? "rgba(34,197,94,0.08)"
                    : "rgba(34,197,94,0.05)"
                  : "transparent",
                transition: "all 0.2s",
                "&:hover": {
                  backgroundColor: darkMode
                    ? "rgba(255,255,255,0.03)"
                    : "rgba(0,0,0,0.02)",
                },
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={modules.includes(mod)}
                    onChange={() => toggleModule(mod)}
                    disabled={!canEdit}
                    size="small"
                    sx={darkMode ? {
                      color: "rgba(255,255,255,0.3)",
                      "&.Mui-checked": { color: "#22C55E" },
                    } : {
                      "&.Mui-checked": { color: "#22C55E" },
                    }}
                  />
                }
                label={
                  <KDTypography
                    variant="body2"
                    fontWeight="medium"
                    color={darkMode ? "white" : "dark"}
                    sx={{ textTransform: "capitalize" }}
                  >
                    {t(`nav.${mod}`) || mod}
                  </KDTypography>
                }
                sx={{ m: 0 }}
              />
            </KDBox>
          </Grid>
        ))}
      </Grid>

      {filteredModules.length === 0 && (
        <KDBox py={3} textAlign="center">
          <KDTypography variant="body2" color="text" sx={{ opacity: 0.5 }}>
            {t("roles.detail.noMatchingModules")}
          </KDTypography>
        </KDBox>
      )}
    </CollapsibleSection>
  );
}

export default ModulesSection;
